import { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@leap4/frontend';
import type { FormulaTokenDto } from '../../../api/generated/models';
import {
  FormulaOperandOption,
  MAX_FORMULA_TOKENS,
  operandOptionKey,
  operandReportTypes,
} from './calculatedMetrics.constants';
import { buildReportTypeIndex, renderReportTypeChips } from './ReportTypeChips';
import { useReportingMetadata } from '../reporting/hooks/useReportingMetadata';

interface FormulaBuilderProps {
  tokens: FormulaTokenDto[];
  operandOptions: FormulaOperandOption[];
  onChange: (tokens: FormulaTokenDto[]) => void;
}

const OPERATORS = ['+', '-', '*', '/'] as const;

const tokenToText = (token: FormulaTokenDto): string => {
  switch (token.t) {
    case 'op':
      return String(token.v);
    case 'lparen':
      return '(';
    case 'rparen':
      return ')';
    case 'num':
      return String(token.v);
    case 'ref':
      if (token.kind === 'sum_event') return `${token.key ?? ''}·$`;
      if (token.kind === 'matched_event') return `${token.key ?? ''}·matched`;
      return String(token.key ?? '');
    default:
      return '?';
  }
};

const buildPreview = (tokens: FormulaTokenDto[]): string => tokens.map(tokenToText).join(' ');

const GROUP_LABELS: Record<FormulaOperandOption['kind'], string> = {
  metric: 'Metrics',
  event: 'Events (attributed)',
  count_event: 'Events (count)',
  sum_event: 'Event $ totals',
  matched_event: 'Matched impressions',
};

const FormulaBuilder = ({ tokens, operandOptions, onChange }: FormulaBuilderProps) => {
  const metadata = useReportingMetadata();
  const reportTypeIndex = useMemo(() => buildReportTypeIndex(metadata), [metadata]);
  const [literalInput, setLiteralInput] = useState('');
  const [operandInput, setOperandInput] = useState('');
  const atCap = tokens.length >= MAX_FORMULA_TOKENS;

  const appendToken = (token: FormulaTokenDto) => {
    if (tokens.length >= MAX_FORMULA_TOKENS) return;
    onChange([...tokens, token]);
  };

  const handlePickOperand = (option: FormulaOperandOption | null) => {
    if (!option) return;
    const isEventKind =
      option.kind === 'event' ||
      option.kind === 'sum_event' ||
      option.kind === 'matched_event';
    appendToken({
      t: 'ref',
      kind: option.kind,
      key: isEventKind ? option.key.trim().toLowerCase() : option.key,
    });
    setOperandInput('');
  };

  const handleAddLiteral = () => {
    const trimmed = literalInput.trim();
    if (!trimmed) return;
    const value = Number(trimmed);
    if (!Number.isFinite(value)) return;
    appendToken({ t: 'num', v: value });
    setLiteralInput('');
  };

  const handleBackspace = () => {
    if (tokens.length === 0) return;
    onChange(tokens.slice(0, -1));
  };

  const handleClear = () => onChange([]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          minHeight: 44,
          px: 1.5,
          py: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'monospace',
          fontSize: '1rem',
          overflowX: 'auto',
          color: tokens.length === 0 ? 'text.secondary' : 'text.primary',
        }}
      >
        {tokens.length === 0 ? 'Build your formula, e.g. ( c4 / c5 ) * 100' : buildPreview(tokens)}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Autocomplete
          size="small"
          options={operandOptions}
          value={null}
          inputValue={operandInput}
          onInputChange={(_event, value, reason) => {
            setOperandInput(reason === 'reset' ? '' : value);
          }}
          onChange={(_event, option) => handlePickOperand(option as FormulaOperandOption | null)}
          getOptionLabel={(option) => (option as FormulaOperandOption).label}
          renderOption={(props, option) => {
            const opt = option as FormulaOperandOption;
            const { key: _key, ...rest } = props as any;
            return (
              <li {...rest} key={operandOptionKey(opt)}>
                <span>{opt.label}</span>
                {renderReportTypeChips(operandReportTypes(opt, reportTypeIndex.get(opt.key)))}
              </li>
            );
          }}
          groupBy={(option) => GROUP_LABELS[(option as FormulaOperandOption).kind]}
          isOptionEqualToValue={(option, value) =>
            operandOptionKey(option as FormulaOperandOption) ===
            operandOptionKey(value as FormulaOperandOption)
          }
          disabled={atCap}
          blurOnSelect
          sx={{ minWidth: 300 }}
          renderInput={(params) => (
            <TextField {...params} label="Add metric / event" placeholder="Type to search…" />
          )}
        />

        <TextField
          label="Number"
          size="small"
          value={literalInput}
          onChange={(e) => setLiteralInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddLiteral();
            }
          }}
          sx={{ width: 120 }}
        />
        <Button
          variant="outlined"
          onClick={handleAddLiteral}
          disabled={!literalInput.trim() || atCap}
        >
          Add number
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {OPERATORS.map((op) => (
          <Button
            key={op}
            variant="outlined"
            onClick={() => appendToken({ t: 'op', v: op })}
            disabled={atCap}
            sx={{ minWidth: 44 }}
          >
            {op}
          </Button>
        ))}
        <Button
          variant="outlined"
          onClick={() => appendToken({ t: 'lparen' })}
          disabled={atCap}
          sx={{ minWidth: 44 }}
        >
          (
        </Button>
        <Button
          variant="outlined"
          onClick={() => appendToken({ t: 'rparen' })}
          disabled={atCap}
          sx={{ minWidth: 44 }}
        >
          )
        </Button>
        <Typography variant="caption" color="textSecondary">
          Build left to right like a calculator. Backspace removes the last item. {tokens.length}/
          {MAX_FORMULA_TOKENS} items.
        </Typography>
        <Box sx={{ flex: 1 }} />

        <Button variant="outlined" onClick={handleBackspace} disabled={tokens.length === 0}>
          Backspace
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleClear}
          disabled={tokens.length === 0}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default FormulaBuilder;
